import { EField as EEpcField } from "@rentiohq/shared-frontend/dist/forms/epc/schema.epcInfo.types";
import { mapFormFieldToNullableBooleanValue } from "@rentiohq/shared-frontend/dist/redux/form/form.utils";
import { IUpdateContractWithId } from "@rentiohq/shared-frontend/dist/reduxV2/contract/contract.types";
import {
  AMENITY_NAMES,
  createAmenityItem,
  IUpsertPropertyAmenityItem,
  prependEANPrefix,
} from "@rentiohq/shared-frontend/dist/reduxV2/propertyAmenity";
import * as rentRegistrationActions from "@rentiohq/shared-frontend/dist/reduxV2/rentRegistration/rentRegistration.actions";
import * as rentRegistrationSelectors from "@rentiohq/shared-frontend/dist/reduxV2/rentRegistration/rentRegistration.selectors";
import { IProperty } from "@rentiohq/shared-frontend/dist/types/property.types";
import {
  ERegistrationContractDestination,
  ERegistrationContractType,
  ERegistrationDocumentType,
  ERegistrationMemberType,
  ICreateRegistration,
  ICreateRegistrationDocument,
} from "@rentiohq/shared-frontend/dist/types/registration.types";
import { hasBrusselsRegistration } from "@rentiohq/shared-frontend/dist/utils/property.utils";
import { IRootStore } from "redux/reducers";
import { EField, IExtraData } from "./schema.registration.types";

export const isPerformingSubmitActionSelector =
  (extraData: IExtraData) => (state: IRootStore) =>
    extraData.registration
      ? rentRegistrationSelectors.isRetryingMyRent(
          state,
          extraData.registration.id,
        )
      : rentRegistrationSelectors.isCreating(state);

export const performingSubmitActionResultSelector =
  (extraData: IExtraData) => (state: IRootStore) =>
    extraData.registration
      ? rentRegistrationSelectors.getMyRentRetryResult(state)
      : rentRegistrationSelectors.getCreatedDetail(state);

export const performingSubmitActionErrorSelector =
  (extraData: IExtraData) => (state: IRootStore) =>
    extraData.registration
      ? rentRegistrationSelectors.myRentRetryError(
          state,
          extraData.registration.id,
        )
      : rentRegistrationSelectors.createError(state);

export const submitActionCreator =
  (extraData: IExtraData) => (formData: any) => {
    const { property, propertyAmenities, contract } = extraData;

    const withPropertyContractUpdate = hasBrusselsRegistration(
      property,
      contract.leaseType,
    );

    const parsedElectricityEan = prependEANPrefix(
      formData[EField.ElectricityEan],
    );
    const parsedNaturalGasEan = prependEANPrefix(
      formData[EField.NaturalGasEan],
    );

    const updatedProperty: Partial<IProperty> | undefined =
      withPropertyContractUpdate
        ? {
            id: property.id,

            // General
            constructionYear: formData[EField.ConstructionYear] ?? null,

            // EPC Fields
            epcLabel: formData[EEpcField.Label] ?? null,
            epcValue: formData[EEpcField.Value] ?? null,
            epcExpirationDate: formData[EEpcField.ExpirationDate] ?? null,
            epcCertificateId: formData[EEpcField.CertificateId] ?? null,

            // Electricity
            electricityType: formData[EField.ElectricityType] ?? null,

            electricityMeterNumberBoth:
              formData[EField.ElectricityMeterNumberBoth] ?? null,
            electricityEan: parsedElectricityEan,
            electricityMeterType: formData[EField.ElectricityMeterType] ?? null,

            // Natural Gas
            naturalGasAvailable: formData[EField.NaturalGasAvailable] ?? null,
            naturalGasEan: parsedNaturalGasEan,
            naturalGasMeterNumber:
              formData[EField.NaturalGasMeterNumber] ?? null,
            naturalGasMeterType: formData[EField.NaturalGasMeterType] ?? null,

            // Water
            waterMeterNumber: formData[EField.WaterMeterNumber] ?? null,

            // Exterior (garden/terrace stay on property)
            buildingGardenSurfaceArea:
              formData[EField.BuildingGardenSurfaceArea] ?? null,
            buildingTerraceSurfaceArea:
              formData[EField.BuildingTerraceSurfaceArea] ?? null,
          }
        : undefined;

    let updatedAmenities: IUpsertPropertyAmenityItem[] | undefined;

    // Build amenity items for upsert to propertyAmenity module
    if (withPropertyContractUpdate) {
      const amenityItems: (IUpsertPropertyAmenityItem | null)[] = [
        // Exterior
        createAmenityItem(
          propertyAmenities,
          AMENITY_NAMES.buildingAdjoined,
          formData[EField.BuildingAdjoined],
          "string",
        ),
        createAmenityItem(
          propertyAmenities,
          AMENITY_NAMES.buildingSurfaceArea,
          formData[EField.BuildingSurfaceArea],
          "number",
        ),
        // Interior
        createAmenityItem(
          propertyAmenities,
          AMENITY_NAMES.buildingFloor,
          formData[EField.BuildingFloor],
          "number",
        ),
        createAmenityItem(
          propertyAmenities,
          AMENITY_NAMES.buildingFloorCount,
          formData[EField.BuildingFloorCount],
          "number",
        ),
        createAmenityItem(
          propertyAmenities,
          AMENITY_NAMES.roomCountBedroom,
          formData[EField.RoomCountBedroom],
          "number",
        ),
        createAmenityItem(
          propertyAmenities,
          AMENITY_NAMES.roomCountBathroom,
          formData[EField.RoomCountBathroom],
          "number",
        ),
        createAmenityItem(
          propertyAmenities,
          AMENITY_NAMES.roomCountGarage,
          formData[EField.RoomCountGarage],
          "number",
        ),
        createAmenityItem(
          propertyAmenities,
          AMENITY_NAMES.buildingHasStorage,
          mapFormFieldToNullableBooleanValue(
            formData[EField.BuildingHasStorage],
          ),
          "boolean",
        ),
        createAmenityItem(
          propertyAmenities,
          AMENITY_NAMES.isFurnished,
          mapFormFieldToNullableBooleanValue(formData[EField.IsFurnished]),
          "boolean",
        ),
        // Amenities
        createAmenityItem(
          propertyAmenities,
          AMENITY_NAMES.hasOven,
          mapFormFieldToNullableBooleanValue(formData[EField.HasOven]),
          "boolean",
        ),
        createAmenityItem(
          propertyAmenities,
          AMENITY_NAMES.hasFridge,
          mapFormFieldToNullableBooleanValue(formData[EField.HasFridge]),
          "boolean",
        ),
        createAmenityItem(
          propertyAmenities,
          AMENITY_NAMES.hasDishwasher,
          mapFormFieldToNullableBooleanValue(formData[EField.HasDishWasher]),
          "boolean",
        ),
        createAmenityItem(
          propertyAmenities,
          AMENITY_NAMES.hasRangeHood,
          mapFormFieldToNullableBooleanValue(formData[EField.HasRangeHood]),
          "boolean",
        ),
        createAmenityItem(
          propertyAmenities,
          AMENITY_NAMES.hasLift,
          mapFormFieldToNullableBooleanValue(formData[EField.HasLift]),
          "boolean",
        ),
        // Energy
        createAmenityItem(
          propertyAmenities,
          AMENITY_NAMES.hasDoubleGlazing,
          mapFormFieldToNullableBooleanValue(formData[EField.HasDoubleGlazing]),
          "boolean",
        ),
        createAmenityItem(
          propertyAmenities,
          AMENITY_NAMES.hasCentralHeating,
          mapFormFieldToNullableBooleanValue(
            formData[EField.HasCentralHeating],
          ),
          "boolean",
        ),
        createAmenityItem(
          propertyAmenities,
          AMENITY_NAMES.heatingType,
          formData[EField.HeatingType],
          "string",
        ),
      ];

      // Filter out null items - only send changed amenities
      const newAmenityItems = amenityItems.filter(
        (item): item is IUpsertPropertyAmenityItem => item !== null,
      );

      // Amenities will be saved by the saga
      if (newAmenityItems.length > 0) {
        updatedAmenities = newAmenityItems;
      }
    }

    const updatedContract: IUpdateContractWithId | undefined =
      withPropertyContractUpdate
        ? {
            id: contract.id,

            [EField.IsSharedLease]: mapFormFieldToNullableBooleanValue(
              formData[EField.IsSharedLease],
            ),
            [EField.ReferencePrice]: formData[EField.ReferencePrice] ?? null,
          }
        : undefined;

    const members = [
      ...(formData[EField.Owners] || []).map((accountId: number) => ({
        role: ERegistrationMemberType.Owner,
        accountId,
      })),
      ...(formData[EField.SignAsBroker] || []).map((accountId: number) => ({
        role: ERegistrationMemberType.Broker,
        accountId,
      })),
      ...(formData[EField.Tenants] || []).map((accountId: number) => ({
        role: ERegistrationMemberType.Tenant,
        accountId,
      })),
    ];

    const registration: ICreateRegistration = {
      contractId: contract.id,
      propertyId: property.id,
      members,
      documents: [],
      rentContractDestination: formData[EField.RentContractDestination],
      preferredContractLanguage: formData[EField.RentContractLanguage],
      rentContractPropertyTaxPaidByTenant:
        formData[EField.RentContractPropertyTaxPaidByTenant] === "yes",
    };

    if (
      formData[EField.RentContractDestination] ===
      ERegistrationContractDestination.Housing
    ) {
      registration.rentContractPropertyTaxPaidByTenant = false;
    }

    const registrationContractDocument: ICreateRegistrationDocument = {
      documentId: formData[EField.RentContractDocumentToUpload]?.[0],
      documentType: ERegistrationDocumentType.Contract,
      type: ERegistrationContractType.Lease,
      signedAt: formData[EField.RentContractSignedAt],
      signers: members,
    };

    registration.documents.push(registrationContractDocument);

    if (formData[EField.LocationDescriptionShouldUpload] === "now") {
      const locationDescriptionDocument: ICreateRegistrationDocument = {
        documentId: formData[EField.LocationDescriptionDocumentToUpload]?.[0],
        documentType: ERegistrationDocumentType.PropertyInspectionReport,
        type: ERegistrationContractType.Lease,
        signedAt: formData[EField.LocationDescriptionSignedAt],
        signers: members,
      };

      registration.documents.push(locationDescriptionDocument);
    }

    if (extraData.registration) {
      return rentRegistrationActions.myRentRegistrationRetryStart.getAction({
        registration,
        registrationId: extraData.registration.id,
        updatedProperty,
        updatedContract,
        updatedAmenities,
      });
    }

    return rentRegistrationActions.createStart.getAction({
      data: {
        ...registration,
        updatedProperty: updatedProperty as any,
        updatedContract: updatedContract as any,
        updatedAmenities,
      },
    });
  };
